import { getEduPortalUrl } from '../../config/api';
import { showCheckoutRefusal } from '../checkout/checkoutRefusal';
import {
  CHECKOUT_MISMATCH_MESSAGE,
  rememberPendingCheckout,
  requestBeginnings,
  requestCheckoutSession,
  tierSlugOf,
} from '../checkout/pendingCheckout';
import {
  ORG_PLAN_NOTICE,
  readSignedInStoredUser,
  userMayPurchase,
} from '../checkout/purchaseEligibility';

function loginPath(tierKey, examId, surface, failed, packageKey, subject) {
  const tier = encodeURIComponent(tierKey);
  const exam = encodeURIComponent(examId);
  const extra =
    (packageKey ? `&package=${encodeURIComponent(packageKey)}` : '') +
    (subject ? `&subject=${encodeURIComponent(subject)}` : '');
  if (surface === 'pricing' && failed) {
    return `/login?tier=${tier}&exam=${exam}${extra}`;
  }
  return `/login?signup=true&tier=${tier}&exam=${exam}${extra}`;
}

const BEGINNINGS_FAILED_MESSAGE = 'Your choice could not be saved. Please try again.';

let checkoutInFlight = false;

/**
 * Checkout for the new marketing pages. `surface: 'exam'` follows the
 * exam landings. `surface: 'pricing'` follows the pricing page.
 *
 * A second call while the Stripe request is in flight returns without
 * posting again. The flag clears when that request settles. Signed-out,
 * Beginnings, and organisation-refusal returns do not take the flag.
 *
 * Beginnings on the pricing page needs a signed-in visitor: signed out goes
 * to sign-up with the pending keys; signed in posts the free choice to
 * POST /api/purchases/beginnings and then opens practice. Signed-in
 * Beginnings on an exam surface opens /practice/{examId}.
 *
 * A paid click posts {exam, tier, package, subject}. The response must echo
 * the package and subject that were sent: when it does not, the visitor is
 * told and is not sent to Stripe.
 */
export async function startCheckout({ tierKey, examId, packageKey, subject, promoCode, surface, navigate, user }) {
  const slug = tierSlugOf(tierKey);
  const beginnings = slug === 'beginnings';
  const pendingArgs = { tierKey, examId, packageKey, subject };
  if (!slug || slug === 'free') return;

  const current = user || readSignedInStoredUser();
  if (!current || !current.email) {
    rememberPendingCheckout(pendingArgs);
    navigate(loginPath(tierKey, examId, surface, false, packageKey, subject));
    return;
  }

  // An organisation member's plan comes from the organisation, so there is
  // nothing to choose (B5 refuses them); the free link just opens practice.
  if (beginnings && (surface === 'exam' || !userMayPurchase(current))) {
    window.location.href = getEduPortalUrl(`/practice/${examId}`);
    return;
  }

  if (!userMayPurchase(current)) {
    showCheckoutRefusal(ORG_PLAN_NOTICE);
    return;
  }

  if (beginnings) {
    const result = await requestBeginnings({ exam: examId, packageKey, subject });
    if (result.status === 'granted') {
      window.location.href = getEduPortalUrl(`/practice/${examId}`);
    } else if (result.status === 'refused') {
      showCheckoutRefusal(result.message);
    } else {
      showCheckoutRefusal(BEGINNINGS_FAILED_MESSAGE);
    }
    return;
  }

  if (checkoutInFlight) return;
  checkoutInFlight = true;

  try {
    const result = await requestCheckoutSession({
      tier: slug,
      exam: examId,
      packageKey,
      subject,
      promoCode,
    });
    if (result.status === 'redirect') {
      window.location.href = result.url;
    } else if (result.status === 'refused') {
      showCheckoutRefusal(result.message);
    } else if (result.status === 'mismatch') {
      showCheckoutRefusal(CHECKOUT_MISMATCH_MESSAGE);
    } else {
      rememberPendingCheckout(pendingArgs);
      navigate(loginPath(tierKey, examId, surface, true, packageKey, subject));
    }
  } finally {
    checkoutInFlight = false;
  }
}
